import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { isNil } from 'lodash';
import { IAiAdjustmentAvailability } from 'src/providers/ai-adjustment.provider';

/**
 * How tall the instruction box may grow before it scrolls, in pixels. Matches
 * `max-height` on `.ai-dock__input`; both have to move together.
 */
const INSTRUCTION_MAX_HEIGHT = 260;

/** What the user asked the AI to do. */
export interface IAiAdjustmentSubmission {
  /** The free-text steering input, absent when the user only applied the vision */
  instruction?: string;
  /** Whether to apply the organization vision as a steering input */
  useOrganizationVision: boolean;
}

/**
 * The AI adjustment input, docked below the activity form.
 *
 * Styled as a chat composer because the prototype is, but deliberately not a
 * chat: one submit produces one proposal, the field clears, and no history is
 * kept. Conversational refinement is explicitly out of scope.
 */
@Component({
  selector: 'app-ai-adjustment-dock',
  templateUrl: './ai-adjustment-dock.component.html',
  styleUrls: ['./ai-adjustment-dock.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AiAdjustmentDockComponent {
  /** What the availability check reported */
  @Input() availability: IAiAdjustmentAvailability | null = null;

  /** True while a request is in flight, or while the user may not edit */
  @Input() disabled = false;

  /** True when the current user may edit this activity at all */
  @Input() allowedToEdit = true;

  @Output() submitted = new EventEmitter<IAiAdjustmentSubmission>();

  @ViewChild('instructionInput') private instructionInput?: ElementRef<HTMLTextAreaElement>;

  public instruction = '';

  public useOrganizationVision = false;

  public visionPreviewOpen = false;

  /** True when the dock should be rendered at all, enabled or not */
  public get visible(): boolean {
    return this.allowedToEdit && this.availability?.visible === true;
  }

  public get organizationVision(): string | null {
    return this.availability?.organizationVision ?? null;
  }

  /** The vision toggle is offered only when there is a vision to apply */
  public get visionAvailable(): boolean {
    return !isNil(this.organizationVision);
  }

  public get budgetRemaining(): number | null {
    return this.availability?.budgetRemaining ?? null;
  }

  /** The date the current window rolls over, for the exhausted-budget message */
  public get budgetResetsAt(): string | null {
    return this.availability?.quota?.end_date ?? null;
  }

  public get blockedReason(): string | null {
    return this.availability?.reason ?? null;
  }

  /** True when the dock is shown but cannot be used */
  public get blocked(): boolean {
    return this.availability?.allowed !== true;
  }

  /**
   * At least one steering input is required, and either alone is enough.
   * The API rejects a request with neither, so the button is the first gate.
   */
  public get canSubmit(): boolean {
    if (this.disabled || this.blocked) {
      return false;
    }

    return this.instruction.trim().length > 0 || this.applyVision;
  }

  /**
   * Whether the vision is actually going to be sent.
   *
   * Availability is refetched after every adjustment, so a vision can be
   * cleared while the box is still ticked. The stored answer is kept as the
   * user left it and read through this, rather than reset behind their back.
   */
  private get applyVision(): boolean {
    return this.useOrganizationVision && this.visionAvailable;
  }

  public toggleVision(checked: boolean): void {
    this.useOrganizationVision = checked;
  }

  public toggleVisionPreview(): void {
    this.visionPreviewOpen = !this.visionPreviewOpen;
  }

  /**
   * Emits the submission and clears the field.
   *
   * `canSubmit` is rechecked here rather than trusted from the template: Enter
   * reaches this method directly, and a double keypress must not send a second
   * request. Every request costs tokens, so the guard has to be here too and
   * not only on the button's disabled state.
   */
  public submit(): void {
    if (!this.canSubmit) {
      return;
    }

    const instruction = this.instruction.trim();

    this.submitted.emit({
      instruction: instruction.length > 0 ? instruction : undefined,
      useOrganizationVision: this.applyVision,
    });
  }

  /**
   * Clears the instruction once the outcome is settled.
   *
   * Not done on submit: a transient failure invites a retry, and clearing the
   * field there would make the user retype what they just wrote.
   */
  public clearInstruction(): void {
    this.instruction = '';
    this.autoGrow();
  }

  /**
   * Grows the textarea with its content.
   *
   * Capped well above a couple of lines: an instruction is prose, not a chat
   * message, and scrolling a box you are still writing in makes it impossible
   * to reread what you typed. Past the cap it scrolls rather than pushing the
   * activity form off screen.
   */
  public autoGrow(): void {
    const element = this.instructionInput?.nativeElement;

    if (isNil(element)) {
      return;
    }

    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, INSTRUCTION_MAX_HEIGHT)}px`;
  }
}
